/**
 * The day's count, said ONCE.
 *
 * Owner, 2026-09-11, reading /today: the masthead said "3 things left today.
 * 1 of 1 habits kept.", the Done card said "4 of 7" over a list of THREE
 * titles, and the Do card's own toggle said "3 of 6 tasks done · 1 of 1
 * habits done". Three readers, two formulas: the masthead unioned ids across
 * completions, calendar rows and the focus plan; the Done ring added counts.
 * They agreed that day by coincidence. And the fourth "done" thing was the
 * habit, which the list under the ring never named.
 *
 * This module is the one formula. Pure and import-free so the node probe
 * (`npm run test:ori`) can pin it without a browser.
 */

export interface DayTallyInput {
  /** Tasks finished today — the list the Done card names. */
  tasksDone: number
  /** Tasks still open on today's plan. */
  tasksOpen: number
  habitsDone: number
  habitsTotal: number
}

export interface DayTally {
  tasksDone: number
  tasksTotal: number
  habitsDone: number
  habitsTotal: number
  /** Tasks and habits finished. */
  done: number
  /** Tasks and habits on the day. */
  total: number
  /** What is still to come — the same number wherever the page says it. */
  left: number
}

const nonNeg = (n: number) => (Number.isFinite(n) && n > 0 ? Math.floor(n) : 0)

export function dayTally(i: DayTallyInput): DayTally {
  const tasksDone = nonNeg(i.tasksDone)
  const tasksOpen = nonNeg(i.tasksOpen)
  const habitsTotal = nonNeg(i.habitsTotal)
  // A habit cannot be kept more times than it exists on the day.
  const habitsDone = Math.min(nonNeg(i.habitsDone), habitsTotal)
  const tasksTotal = tasksDone + tasksOpen
  const done = tasksDone + habitsDone
  const total = tasksTotal + habitsTotal
  return { tasksDone, tasksTotal, habitsDone, habitsTotal, done, total, left: total - done }
}

/**
 * The masthead's one sentence about the day. Habits kept is appended by the
 * caller only when there is something kept to say.
 */
export function dayHeadline(t: DayTally, resting: boolean): string {
  if (resting) return 'Resting for today — see you tomorrow.'
  if (t.total === 0) return 'Nothing planned yet.'
  if (t.left === 0) return 'Everything for today is done.'
  return `${t.left} thing${t.left === 1 ? '' : 's'} left today.`
}

/** " 1 of 1 habits kept." — or nothing, when nothing is kept yet. */
export function habitsKeptWords(t: DayTally): string {
  if (t.habitsTotal === 0 || t.habitsDone === 0) return ''
  return ` ${t.habitsDone} of ${t.habitsTotal} habits kept.`
}

/** "2 hr 15 min" · "45 min" · "3 hr" — the metadata contract's duration shape. */
export function freeWords(minutes: number): string {
  const m = Math.max(0, Math.round(minutes))
  const h = Math.floor(m / 60)
  const r = m % 60
  if (h === 0) return `${r} min`
  if (r === 0) return `${h} hr`
  return `${h} hr ${r} min`
}

/**
 * The header pill (2026-09-12 mockup: "1 Task Remaining · 11h Free Time").
 * Reads the SAME tally sentence as the masthead did, so the count keeps one
 * expression, and adds how much of the day is still open — only while
 * something is left to put in it. A finished or resting day says so and
 * stops there; free time on a done day is not a fact anyone acts on.
 */
export function dayPillWords(t: DayTally, freeMinutes: number | null, resting: boolean): string {
  const head = dayHeadline(t, resting)
  if (resting || t.left === 0 || freeMinutes === null || freeMinutes <= 0) return head
  return `${head} About ${freeWords(freeMinutes)} open.`
}
